import {
  AccessToken,
  GameRequestDialog,
  LoginManager,
} from "react-native-fbsdk-next";
import API from "../utils/API";
import {
  axiosInstance,
  bowaApi,
  casinoApi,
  svaraApi,
} from "../utils/axiosInstance";
import LS from "../utils/localStorageUtils";
import { Platform } from "react-native";
import { env } from "../utils/env";

export function useAPIAuthRequest() {
  async function AuthRequestToApi(endpoint, method, data, headers) {
    return API.authRequest(endpoint, method, data, {
      "Content-Type": "application/json",
      ...API.getXCasinoHeader(),
    });
  }
  return { AuthRequestToApi };
}

export async function getUserProfile() {
  try {
    const { data } = await svaraApi.get("/api/player/profile");
    return data.data;
  } catch (err) {
    console.error("Failed to load player profile", err);
    throw err;
  }
}

export type TournamentStatisticsBreakdown = {
  "1": number;
  "2": number;
  "3": number;
  total: number;
};

export type MyTournamentStatistics = {
  gold: TournamentStatisticsBreakdown;
  silver: TournamentStatisticsBreakdown;
  bronze: TournamentStatisticsBreakdown;
};

// Response shape example:
// { name: "OK", status: 200, data: { gold: { "1":0,"2":0,"3":0,total:0 }, ... } }
export async function getMyTournamentStatistics() {
  try {
    const { data } = await svaraApi.get("/api/player/tournaments/my_statistics");
    return data.data as MyTournamentStatistics;
  } catch (err) {
    console.error("Failed to load my tournament statistics", err);
    throw err;
  }
}

export async function getLobbyRooms(params: { start: number; limit: number }) {
  try {
    const response = await svaraApi.get("/api/lobby/rooms", {
      params: { _start: params.start, _end: params.start + params.limit },
    });
    return response;
  } catch (err: any) {
    if (err.response?.status === 401) {
      return Promise.reject({
        ...err,
        skipNavigation: true,
      });
    }
    throw err;
  }
}

export async function createSpecialTable(data: {
  playerId: string;
  stake: number;
  minimalAmount: number;
  maximalAmount: number;
  maxSeats: number;
  turnDuration: number;
  isPrivate: boolean;
}) {
  try {
    const response = await svaraApi.post("/api/lobby/tables/special", data);
    console.log("response data from VIP table", response.data);
    return response.data;
  } catch (err: any) {
    console.error("[createSpecialTable] error:", err);
    throw err;
  }
}

export async function getMyActiveTables() {
  try {
    const response = await svaraApi.get("/api/lobby/tables/my_active_tables");

    return response;
  } catch (err: any) {
    console.error("Failed to load my tables", err);
    throw err;
  }
}

export async function getActivePlayers(params: {
  start: number;
  limit: number;
  tableId?: string | number;
}) {
  try {
    const response = await svaraApi.get("/api/player/players/active", {
      params: {
        _start: params.start,
        _end: params.start + params.limit,
        tableId: params.tableId,
      },
    });
    return response;
  } catch (err: any) {
    if (err.response?.status === 401) {
      return Promise.reject({
        ...err,
        skipNavigation: true,
      });
    }
    throw err;
  }
}

export async function getActiveUsersCounter() {
  try {
    const response = await casinoApi.get("/api/lobby/users/active/counter");
    console.log("active users count", response.data);
    return response.data;
  } catch (err: any) {
    // console.error("[ActiveUsers] error:", err);
    throw err;
  }
}

export async function getUserStatistics() {
  try {
    const userStr = LS.get("auth_user");
    const user = userStr ? JSON.parse(userStr) : null;

    const response = await axiosInstance.get(
      `/api/player/statistics/${user?.id}`,
      {
        params: { gameType: "SVARA" },
      },
    );
    return response;
  } catch (err: any) {
    if (err.response?.status === 401) {
      return Promise.reject({
        ...err,
        skipNavigation: true,
      });
    }
    throw err;
  }
}

export async function collectDeltaDNA(endpoint: string, data?: any) {
  try {
    const response = await svaraApi.post(
      `/api/deltadna/collect/${endpoint}`,
      data || {},
    );
    console.log("collect delta dna", response.data);
    return response.data;
  } catch (err: any) {
    console.error("[Collect DELTA DNA] error:", err);
    throw err;
  }
}

export async function engageDeltaDNA(endpoint: string) {
  try {
    const response = await svaraApi.get(`/api/deltadna/engage/${endpoint}`);
    console.log("engage delta dna GET", response.data);
    return response.data;
  } catch (err: any) {
    console.error("[Engage DELTA DNA GET] error:", err);
    throw err;
  }
}

export async function createPlayerReport(data: {
  reporterId: string;
  reportedId: string;
  reportType: string;
  reason: string;
  gameContext: string;
}) {
  try {
    const response = await svaraApi.post("/api/player/reports", data);
    return response.data;
  } catch (err: any) {
    console.error("[createPlayerReport] error:", err);
    throw err;
  }
}

export async function getPackages(params: { start: number; limit: number }) {
  try {
    const response = await axiosInstance.get("/api/store/packages", {
      params: {
        _start: params.start,
        _end: params.start + params.limit,
        gameType: "SVARA",
      },
    });
    return response;
  } catch (err: any) {
    if (err.response?.status === 401) {
      return Promise.reject({
        ...err,
        skipNavigation: true,
      });
    }
    throw err;
  }
}

export async function getPackageById(id: string) {
  try {
    const response = await axiosInstance.get(`/api/store/package/${id}`);
    return response;
  } catch (err: any) {
    if (err.response?.status === 401) {
      return Promise.reject({
        ...err,
        skipNavigation: true,
      });
    }
    throw err;
  }
}

export async function updateProfile(profileData: any, remarks?: string) {
  try {
    const response = await svaraApi.patch("/api/player/profile", profileData);
    return response.data;
  } catch (err: any) {
    console.error("[updateProfile] error:", err);
    throw err;
  }
}

export async function getBonus() {
  try {
    const response = await svaraApi.get("/api/lobby/bonus/periodic");

    return response.data;
  } catch (err: any) {
    console.error("[bonus] error:", err);
    throw err;
  }
}

export async function collectBonus() {
  try {
    const response = await svaraApi.patch("/api/lobby/bonus/periodic");
    return response.data;
  } catch (err: any) {
    console.error("[bonus] error:", err);
    throw err;
  }
}

export async function getMiniGames() {
  try {
    const response = await svaraApi.get("/api/game/games");
    return response.data;
  } catch (err: any) {
    console.error("[miniGames] error:", err);
    throw err;
  }
}

export async function getMiniGamesRewards() {
  try {
    const response = await svaraApi.get("/api/game/games/rewards");
    return response.data;
  } catch (err: any) {
    console.error("[miniGamesRewards] error:", err);
    throw err;
  }
}

export async function getJackpotHistory() {
  try {
    const response = await svaraApi.get("/api/game/games/jackpot_history");
    return response.data;
  } catch (err: any) {
    console.error("[jackpotHistory] error:", err);
    throw err;
  }
}

export async function getNotifications(
  playerId: string,
  params?: {
    _start?: number;
    _end?: number;
    search?: string;
  },
) {
  const query = {
    _start: typeof params?._start === "number" ? params._start : 0,
    _end: typeof params?._end === "number" ? params._end : 50,
    ...(params?.search ? { search: params.search } : {}),
  };
  const response = await svaraApi.get(`/api/notifications/${playerId}`, {
    params: query,
  });

  return response.data;
}

export async function deleteNotificationRead(notificationId: string) {
  const { data } = await svaraApi.delete(
    `/api/notifications/${notificationId}`,
  );
  return data;
}

export async function patchNotificationSeen(
  notificationId: string,
  playerId: string,
  isMassSeen?: boolean,
) {
  const { data } = await svaraApi.patch(
    `/api/notifications/${notificationId}/${playerId}`,
    { isSeen: true, isMassSeen: isMassSeen ?? false },
  );
  return data;
}

export async function fetchTranslations(langId: number) {
  try {
    const response = await svaraApi.get(`/api/svara/translations/${langId}`);

    return response.data.data;
  } catch (err: any) {
    console.error("[translation] error:", err);
    throw err;
  }
}

export async function sendFacebookInvitations(recipientIds: string[]) {
  if (!recipientIds?.length) return [];

  try {
    const responses = await Promise.all(
      recipientIds.map((facebook_id) =>
        svaraApi.post("/api/svara/facebook/invitation", { facebook_id }),
      ),
    );

    return responses.map((r) => r.data);
  } catch (err: any) {
    console.error("[invite] error sending invitations:", err);
    throw err;
  }
}

export async function fetchGeneralRankings(params?: {
  _start?: number;
  _end?: number;
  search?: string;
}) {
  return svaraApi.get("/api/svara/rankings/general", { params });
}

// ---------------------------------------------------------------------------
// Ad Reward
// ---------------------------------------------------------------------------

export async function getAdRewardStatus(): Promise<{
  hasPendingReward: boolean;
  chips: number;
  state: "created" | "approved" | null;
  cooldownMinutesLeft: number;
}> {
  const response = await svaraApi.get("/api/lobby/bonus/ad_reward");
  return response.data?.data;
}

export async function claimAdReward(): Promise<{
  chips: number;
  state: "approved";
}> {
  const response = await svaraApi.patch("/api/lobby/bonus/ad_reward");
  return response.data?.data;
}

export function getRankingsByPeriod(params: {
  period: "Daily" | "Weekly" | "Monthly";
  _start?: number;
  _end?: number;
}) {
  return svaraApi.get("/api/svara/rankings/byPeriod", { params });
}

export function getRankingsTournaments(params: {
  _start?: number;
  _end?: number;
}) {
  return svaraApi.get("/api/svara/rankings/tournaments", { params });
}

export async function socialLoginGoogle(idToken: string) {
  const casinoId = env("CASINO_ID");

  const requestBody = { idToken, socialPlatform: "Google" as const };

  const res = await svaraApi.post("/api/svara/auth/socialLogin", requestBody, {
    headers: {
      "Content-Type": "application/json",
      "x-casino-context": JSON.stringify({ casinoId }),
    },
  });

  return res.data;
}

export async function socialLoginFacebook(accessToken: string) {
  const casinoId = env("CASINO_ID");
  console.log(accessToken);

  const body = { idToken: accessToken, socialPlatform: "Facebook" as const };

  const res = await svaraApi.post("/api/svara/auth/socialLogin", body, {
    headers: {
      "Content-Type": "application/json",
      "x-casino-context": JSON.stringify({ casinoId }),
    },
  });

  return res.data;
}

export const refreshTokenRequest = async (refresh_token: string) => {
  const response = await API.authRequest(
    "/users/auth/token",
    "post",
    { refresh_token },
    { "Content-Type": "application/json", ...API.getXCasinoHeader() },
  );

  if (response.Error) throw response.Error;

  return response.Data?.data || response.Data;
};
